import { Redirect } from 'expo-router';
import { AlertTriangle, Plus, RefreshCw, Search } from 'lucide-react-native';
import { useState } from 'react';
import { ActivityIndicator, Alert, StyleSheet } from 'react-native';
import { Box, HStack, Text, VStack } from '../../../src/components/primitives';
import { Button } from '../../../src/components/primitives/Button';
import { EmptyState } from '../../../src/components/primitives/EmptyState';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useTranslation } from '../../../src/contexts/TranslationContext';
import { SegmentCriteriaForm } from '../../../src/features/linkedin-search/components/SegmentCriteriaForm';
import { SegmentList } from '../../../src/features/linkedin-search/components/SegmentList';
import { SegmentResults } from '../../../src/features/linkedin-search/components/SegmentResults';
import {
  useCreateSegment,
  useDeleteSegment,
  useExecuteSegment,
  useSegmentResults,
  useSegments,
} from '../../../src/features/linkedin-search/hooks/useLinkedInSearch';
import type {
  LinkedInSearchSegment,
  SegmentCriteria,
} from '../../../src/features/linkedin-search/types';
import { colors } from '../../../src/theme/colors';

export default function LinkedInSearchScreen() {
  const { isAuthenticated, isLoading: authLoading, currentOrganization } = useAuth();
  const { t } = useTranslation();

  const orgId = currentOrganization?.id;

  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState<LinkedInSearchSegment | null>(null);
  const [executingId, setExecutingId] = useState<string | undefined>();

  const { data: segments, isLoading, isError, error, refetch } = useSegments(orgId);
  const createSegment = useCreateSegment();
  const deleteSegment = useDeleteSegment();
  const executeSegment = useExecuteSegment();
  const { data: results } = useSegmentResults(selectedSegment?.id, orgId);

  function handleCreate(name: string, criteria: SegmentCriteria) {
    if (!orgId) return;
    createSegment.mutate(
      { organizationId: orgId, name, criteria },
      { onSuccess: () => setShowCreateSheet(false) },
    );
  }

  function handleExecute(segment: LinkedInSearchSegment) {
    if (!orgId) return;
    setExecutingId(segment.id);
    executeSegment.mutate(
      { id: segment.id, organizationId: orgId, data: { skipExisting: true } },
      { onSettled: () => setExecutingId(undefined) },
    );
  }

  function handleDelete(segment: LinkedInSearchSegment) {
    if (!orgId) return;
    Alert.alert('Delete Segment', 'Are you sure? This will delete the segment and all its data.', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: () => {
          if (selectedSegment?.id === segment.id) {
            setSelectedSegment(null);
          }
          deleteSegment.mutate({ id: segment.id, organizationId: orgId });
        },
      },
    ]);
  }

  function handleViewResults(segment: LinkedInSearchSegment) {
    setSelectedSegment(segment);
  }

  if (authLoading) {
    return (
      <Box flex={1} alignItems="center" justifyContent="center">
        <ActivityIndicator size="large" color={colors.primary[500]} />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Redirect href="/login" />;
  }

  return (
    <>
      <Box flex={1} backgroundColor={colors.background.primary} padding="lg">
        <VStack spacing="lg" style={styles.container}>
          <HStack spacing="md" align="center" justify="between">
            <VStack spacing="xxs">
              <Text variant="h2" color={colors.text.primary}>
                {t('linkedinSearch.title')}
              </Text>
              <Text variant="caption" color={colors.text.tertiary}>
                {t('linkedinSearch.description')}
              </Text>
            </VStack>
            <Button variant="primary" size="sm" onPress={() => setShowCreateSheet(true)}>
              <HStack spacing="xs" align="center">
                <Plus size={14} color={colors.background.primary} strokeWidth={2.5} />
                <Text variant="caption" color={colors.background.primary} style={styles.buttonText}>
                  {t('linkedinSearch.newSegment')}
                </Text>
              </HStack>
            </Button>
          </HStack>

          {selectedSegment && (
            <Box>
              <HStack spacing="sm" align="center" style={styles.backRow}>
                <Box
                  pressable
                  onPress={() => setSelectedSegment(null)}
                  borderRadius="md"
                  padding="xs"
                  backgroundColor={colors.background.tertiary}
                >
                  <Text variant="caption" color={colors.primary[400]}>
                    {t('linkedinSearch.backToSegments')}
                  </Text>
                </Box>
              </HStack>
              <SegmentResults leads={results || []} segmentName={selectedSegment.name} />
            </Box>
          )}

          {!selectedSegment && isLoading && (
            <Box flex={1} alignItems="center" justifyContent="center" style={styles.loadingBox}>
              <ActivityIndicator size="large" color={colors.primary[500]} />
            </Box>
          )}
          {!selectedSegment && !isLoading && isError && (
            <Box flex={1} alignItems="center" justifyContent="center" style={styles.loadingBox}>
              <VStack spacing="md" style={styles.errorContainer}>
                <AlertTriangle size={32} color={colors.status.error.main} strokeWidth={1.5} />
                <Text variant="body" color={colors.text.secondary} style={styles.errorText}>
                  {error instanceof Error ? error.message : 'Failed to load segments.'}
                </Text>
                <Button variant="secondary" size="sm" onPress={() => refetch()}>
                  <HStack spacing="xs" align="center">
                    <RefreshCw size={14} color={colors.text.primary} strokeWidth={2} />
                    <Text variant="caption" color={colors.text.primary}>
                      Retry
                    </Text>
                  </HStack>
                </Button>
              </VStack>
            </Box>
          )}
          {!selectedSegment && !isLoading && !isError && (!segments || segments.length === 0) && (
            <EmptyState
              icon={<Search size={32} color={colors.text.tertiary} strokeWidth={1.5} />}
              title={t('linkedinSearch.emptyTitle')}
              description={t('linkedinSearch.emptyDescription')}
            />
          )}
          {!selectedSegment && !isLoading && !isError && segments && segments.length > 0 && (
            <SegmentList
              segments={segments}
              onExecute={handleExecute}
              onDelete={handleDelete}
              onViewResults={handleViewResults}
              isExecuting={executingId}
            />
          )}
        </VStack>
      </Box>

      <SegmentCriteriaForm
        visible={showCreateSheet}
        onClose={() => setShowCreateSheet(false)}
        onSubmit={handleCreate}
        isLoading={createSegment.isPending}
      />
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  buttonText: {
    fontWeight: '700',
  },
  loadingBox: {
    minHeight: 200,
  },
  backRow: {
    marginBottom: 12,
  },
  errorContainer: {
    alignItems: 'center',
  },
  errorText: {
    textAlign: 'center',
  },
});
